/**
 * IDE prefetch.
 *
 * The Workspace route is lazy (see App.tsx), so without this a visitor who
 * submits a prompt has to download the IDE route chunk before anything happens.
 * These helpers warm that chunk while they're still on the landing page, so the
 * transition is instant.
 *
 * Two triggers, neither of which competes with the landing's critical load:
 *   - prefetchOnIntent(): fires the moment the user focuses/typing the prompt
 *     (strong intent, and the page is already interactive by then).
 *   - scheduleIdlePrefetch(): a backstop that waits for `load` and then fetches
 *     during browser idle time, so it never delays first paint or hero assets.
 *
 * Two chunks must be warm for the IDE to open with no network wait:
 *   1. The Workspace route chunk (`WorkspaceRoute.js`) — the route component, the
 *      IDE-only bonds, and the `@molecule/app-ide-react` UI. Importing it here
 *      resolves to the same chunk React.lazy loads, so prefetch and the lazy
 *      route share one download and one cache.
 *   2. The Monaco core (~1 MB), which `MonacoEditorProvider.mount()` pulls in via
 *      its own deeper `import('monaco-editor')`. Warming the route chunk alone
 *      does NOT fetch it, so we call `preloadMonaco()` to warm that exact chunk
 *      too — otherwise the editor would still block on Monaco at mount time.
 */

import { holdWhileReloading } from './chunk-reload.js'

// LIVE (2026-08-08): submitting the prompt opens the IDE for everyone. This was
// `import.meta.env.DEV` pre-launch, when production collected emails via the
// early-access modal instead. The IDE prefetch keys off this too. To revert to
// email-collection, set this back to `import.meta.env.DEV`.
export const PROMPT_OPENS_IDE = true

let inFlight: Promise<unknown> | null = null

/**
 * Idempotently warm everything the IDE needs to open instantly: the Workspace
 * route chunk first, then the Monaco core. Safe to call repeatedly.
 */
export function prefetchWorkspace(): Promise<unknown> {
  if (!inFlight) {
    inFlight = import('../pages/WorkspaceRoute.js')
      .then(() =>
        // Resolves to the same module the Workspace bonds use; preloadMonaco()
        // triggers the `import('monaco-editor')` chunk mount() also awaits.
        import('@molecule/app-code-editor-monaco').then((m) => m.preloadMonaco()),
      )
      // A stale tab's chunk resolves to `undefined` while it reloads.
      .catch(holdWhileReloading)
  }
  return inFlight
}

/** Don't prefetch on data-saver or very slow connections. */
function shouldSkip(): boolean {
  const conn = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection
  if (!conn) return false
  if (conn.saveData) return true
  return /(^|-)2g$/.test(conn.effectiveType || '')
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  cancelIdleCallback?: (id: number) => void
}

/** Prefetch immediately on strong intent (e.g. focusing the prompt input). */
export function prefetchOnIntent(): void {
  if (!PROMPT_OPENS_IDE || shouldSkip()) return
  prefetchWorkspace()
}

/**
 * Schedule a low-priority idle prefetch. Waits for `load` first so it can't
 * compete with the landing's critical resources. Returns a cleanup function.
 */
export function scheduleIdlePrefetch(): () => void {
  if (!PROMPT_OPENS_IDE || shouldSkip()) return () => {}
  const w = window as IdleWindow
  let idleId: number | undefined
  const onLoad = (): void => {
    const ric =
      w.requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 1200) as unknown as number)
    idleId = ric(() => void prefetchWorkspace(), { timeout: 4000 })
  }
  if (document.readyState === 'complete') onLoad()
  else window.addEventListener('load', onLoad, { once: true })
  return () => {
    window.removeEventListener('load', onLoad)
    if (idleId != null) w.cancelIdleCallback?.(idleId)
  }
}
